/**
 * Segmented Control Component
 *
 * A segmented control for selecting between multiple options. Generic over
 * the option-id union so call sites with a typed value (e.g. Home's Scope)
 * get compile-time checking instead of an `as` cast on change.
 *
 * Segments share the width equally (`flex-1`, i.e. basis 0): in a `w-full`
 * container every tab gets the same share (the iOS segmented-control look);
 * in a shrink-wrapped inline container the intrinsic width resolves to
 * N × the widest label, so segments equalise there too — one uniform
 * appearance at every call site.
 */

import { cn } from '@/lib/utils';

interface SegmentedControlOption<T extends string> {
  id: T;
  label: string;
}

interface SegmentedControlProps<T extends string> {
  value: T;
  options: SegmentedControlOption<T>[];
  onChange: (value: T) => void;
  className?: string;
}

export function SegmentedControl<T extends string>({
  value,
  options,
  onChange,
  className,
}: SegmentedControlProps<T>) {
  return (
    <div className={cn('inline-flex rounded-lg bg-neutral-100 p-1', className)}>
      {options.map((option) => (
        <button
          key={option.id}
          onClick={() => onChange(option.id)}
          className={cn(
            'flex-1 px-3 py-1.5 text-sm font-medium text-center rounded-md transition-colors',
            value === option.id
              ? 'bg-white text-neutral-900 shadow-sm'
              : 'text-neutral-600 hover:text-neutral-900'
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
